import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Search, X } from 'lucide-react'
import { HighlightMatch } from '@/shared/components/ui'
import { useAccess } from '@/shared/rbac/use-access'
import { canAdvancedChartSearch } from '@/shared/rbac/capabilities'
import { apiErrorMessage } from '@/shared/utils/api-error'
import {
  CATEGORY_KEYS,
  categoryKey,
  fetchGlobalSearch,
  flattenResults,
  SEARCH_LIMIT,
  SEARCH_MAX_LIMIT,
  useDebouncedValue,
  type CategoryData,
  type GlobalSearchResult,
  type SearchTab,
} from '../hooks/use-global-search'
import {
  CHART_ID_PAGE_SIZE,
  FIELD_LABELS,
  PREFIX_MIN_LEN,
  chartIdOptionsQuery,
  effectiveField,
  useChartFilters,
  chipFilters,
  useChartIdOptions,
  useChartIdSearch,
  useSearchIdentity,
  type ChartIdOperator,
  type ChartIdSearchRequest,
  type IdField,
} from '../hooks/use-chart-id-search'
import { useSearchStore } from '../stores/search.store'
import {
  MAX_VALUE_LENGTH,
  isIdentifierList,
  listKind,
  parseIdentifiers,
  singleQuery,
  uniqueIdentifiers,
  type CommaAs,
} from '../utils/parse-identifiers'
import { CHART_BUCKET_LABELS, type ChartBucketKey } from '@/features/workspace/utils/status-map'
import { MOD_KEY } from '../utils/mod-key'
import { getResultRoute } from '../utils/result-route'
import { chartRoute, plural, useChartRow } from '../hooks/use-chart-row'
import { ChartIdResults } from './chart-id-results'

/** Every category, always inline: the panel is sized (CSS) so all nine fit on one row. */
const TABS: SearchTab[] = ['all', 'encounter', 'icd', 'cpt', 'hcpcs', 'modifier', 'facility', 'provider', 'specialty']
const TAB_LABEL: Record<SearchTab, string> = {
  all: 'All', encounter: 'Charts', icd: 'ICD', cpt: 'CPT', hcpcs: 'HCPCS',
  modifier: 'Modifier', facility: 'Facility', provider: 'Provider', specialty: 'Specialty',
}
/** Charts first: observed use is charts by ID, then codes. */
const GROUP_ORDER: (keyof CategoryData)[] = ['encounter', ...CATEGORY_KEYS.filter((k) => k !== 'encounter')]
/**
 * Rows shown per group on All. The All request fetches SEARCH_LIMIT (12); a
 * category tab fetches TAB_LIMIT, and its "Show more" SEARCH_MAX_LIMIT (50). A
 * count is shown only when it is exact for the request on screen.
 */
const ALL_SHOW = 5
const TAB_LIMIT = 20
/** Tallest the header field grows in list mode (~6 lines) before it scrolls. */
const FIELD_MAX = 140
/** Dropdown width and its minimum gap to the viewport edges, in CSS px. */
const PANEL_GUTTER = 12
const CHART_REVIEW = /^\/workspace\/queue\/[^/]+$/

type Section = 'facility' | 'status' | 'match'
/** A long chip value keeps this many end characters whole; only the start ellipsizes (CSS). */
const CHIP_TAIL = 12

type Option =
  | { kind: 'result'; id: string; result: GlobalSearchResult }
  // 'tab': All's "See all in X" switches to that tab; 'expand': a tab's "Show more" loads up to 50.
  | { kind: 'more'; id: string; tab: SearchTab; label: string; action: 'tab' | 'expand' }

/**
 * The header's search. The header field IS the input (a one-row textarea with
 * combobox semantics); results open in a dropdown anchored under it. A single
 * value shows live grouped results; a pasted list (2+ values) expands the same
 * field in place (overlaying, so the header never grows) and switches to bulk
 * chart-ID search. Cmd/Ctrl+K focuses the field — except on chart review, where
 * Cmd+K is "next chart".
 *
 * Shipped with the header chunk on purpose: no lazy route between a keystroke
 * and its results. Text, filters and the bulk request live in the memory-only
 * search store — never the URL or browser storage.
 */
export function GlobalSearch() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const canFilter = canAdvancedChartSearch(useAccess())
  const store = useSearchStore()
  const { open, text, commaAs, tab, match, facilityIds, buckets, request, showBulk, patch } = store
  const uid = useId()
  const listId = `${uid}-list`
  const rootRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const fieldRef = useRef<HTMLTextAreaElement>(null)
  /** Where the final Escape sends focus: the form field Cmd/Ctrl+K was pressed in. */
  const returnTo = useRef<HTMLElement | null>(null)
  /** A programmatic focus of the field must not reopen the dropdown it just closed. */
  const quiet = useRef(false)
  /** Enter pressed before the results caught up with the text: run it once they do. */
  const pendingEnter = useRef<{ key: string; newTab: boolean } | null>(null)
  /** Focus the field after the next render ('end' also puts the caret after the text). */
  const focusNext = useRef<'end' | 'keep' | null>(null)
  const chipRefs = useRef<Partial<Record<Section, HTMLButtonElement | null>>>({})
  const tabRefs = useRef<Partial<Record<SearchTab, HTMLButtonElement | null>>>({})
  const [section, setSection] = useState<Section | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [panelLeft, setPanelLeft] = useState<number | null>(null)
  // The highlighted option, remembered per (query, tab, mode): a new search starts at the top.
  const [activeFor, setActiveFor] = useState({ key: '', idx: 0 })
  const onChartReview = CHART_REVIEW.test(location.pathname)
  /** Every search query is keyed on who asks (impersonation, client switch): never another identity's cache. */
  const identity = useSearchIdentity()

  const prefetch = () => {
    // Warms the list for the open (which still refetches: grants can change); 30 s keeps hovers from refetching.
    if (canFilter) void queryClient.prefetchQuery({ ...chartIdOptionsQuery(identity), staleTime: 30_000 })
  }

  // ── Options: which ID fields, facilities and Status categories this user may use ──
  const options = useChartIdOptions(open && canFilter)
  const fieldOpts = options.data?.fields ?? []
  const fields = fieldOpts.map((f) => f.field)
  const chartIds = canFilter && fields.length > 0
  // Never a field this identity may not search (a stale choice falls back to Claim ID).
  const field: IdField | undefined = effectiveField(store.field, fields)

  // ── Mode ──
  const multi = canFilter && isIdentifierList(text, commaAs)
  // Comma-only text that did not read as IDs ("Smith, John"): offer "Search as N IDs".
  const commaText = canFilter && listKind(text) === 'commas'
  const trimmed = singleQuery(text)
  const q = useDebouncedValue(multi ? '' : trimmed, 150)
  const single = !multi && q.length >= 2
  // Facility / Status chips: in single mode they narrow the quick search's chart rows.
  const chipFiltered = canFilter && (facilityIds.length > 0 || buckets.length > 0)
  const chartFilters = useChartFilters(canFilter)

  // ── Single mode: live grouped results ──
  const tabCat = tab === 'all' ? null : tab
  const allQ = useQuery({
    queryKey: ['global-search', q, identity],
    queryFn: ({ signal }) => fetchGlobalSearch(q, SEARCH_LIMIT, undefined, signal),
    enabled: open && single,
    staleTime: 30_000,
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey.at(-1) === identity ? prev : undefined), // never another identity's rows
  })
  // ONE matcher: filtered chart rows are the same quick search with the filters
  // added (backend AND predicates), so they are the unfiltered rows, narrowed.
  const filteredCharts = chipFiltered && single && (tab === 'all' || tab === 'encounter')
  const chartF = filteredCharts ? chartFilters : null
  const tabQOn = single && tabCat !== null
  const tabF = tabCat === 'encounter' ? chartF : null
  const tabQ = useQuery({
    queryKey: [...(tabCat ? categoryKey(q, tabCat, TAB_LIMIT, tabF) : ['global-search', q, null]), identity],
    queryFn: ({ signal }) => fetchGlobalSearch(q, TAB_LIMIT, tabCat!, signal, tabF),
    enabled: open && tabQOn,
    staleTime: 30_000,
    // Dim the previous query's rows while typing — but never show another tab's.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === tabCat && prevQuery.queryKey.at(-1) === identity ? prev : undefined),
  })
  // All with a chart filter: the chart group's rows.
  const chartQOn = filteredCharts && tab === 'all' && !!chartF
  const chartQ = useQuery({
    queryKey: [...categoryKey(q, 'encounter', SEARCH_LIMIT, chartF), identity],
    queryFn: ({ signal }) => fetchGlobalSearch(q, SEARCH_LIMIT, 'encounter', signal, chartF),
    enabled: open && chartQOn,
    staleTime: 30_000,
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey.at(-1) === identity ? prev : undefined), // never another identity's rows
  })
  // A tab's "Show more": the same request at the backend max, inside the panel.
  // Remembered per (query, tab, filters), so a new search starts collapsed again.
  const moreKey = `${q}|${tabCat}|${JSON.stringify(tabF)}`
  const [expandedKey, setExpandedKey] = useState('')
  const expanded = tabQOn && expandedKey === moreKey
  const moreQ = useQuery({
    queryKey: [...(tabCat ? categoryKey(q, tabCat, SEARCH_MAX_LIMIT, tabF) : ['global-search', q, null, SEARCH_MAX_LIMIT]), identity],
    queryFn: ({ signal }) => fetchGlobalSearch(q, SEARCH_MAX_LIMIT, tabCat!, signal, tabF),
    enabled: open && expanded,
    staleTime: 30_000,
  })
  /** The open tab's rows: the 50-row request once it is in, else the first 20 (kept on screen meanwhile). */
  const tabData = expanded && moreQ.data ? moreQ.data : tabQ.data
  /** Expanded to the backend max and more still match: say so instead of another "Show more". */
  const capped = !!tabCat && expanded && !!moreQ.data?.[tabCat]?.has_more
  /** The request whose chart rows are on screen when filtered. */
  const chartSrc = tabCat === 'encounter' ? tabQ : chartQ
  const chartSrcData = tabCat === 'encounter' ? tabData : chartQ.data

  // Bulk: chips as Advanced Search filters.
  const buildFilters = (fac = facilityIds, st = buckets) => chipFilters(fac, st, options.data)

  // ── Bulk mode ──
  // The backend refused the field (options stale for this identity): back to the
  // default field and this identity's options fetched again — once per refusal, as
  // only a new Search sends another request.
  const refetchOptions = options.refetch
  const bulk = useChartIdSearch(multi && showBulk ? request : null, () => {
    patch({ field: null, showBulk: false })
    setFormError('That ID field isn’t available for your access. Choose a field and search again.')
    void refetchOptions()
  })
  const values = multi ? parseIdentifiers(text) : []
  const unique = uniqueIdentifiers(values, field)
  const duplicates = values.length - unique.length
  const maxValues = options.data?.max_values ?? 100
  const label = field ? FIELD_LABELS[field] : 'identifier'
  const bulkOp: ChartIdOperator = match ?? 'exact'
  const prefixAllowed = fieldOpts.find((f) => f.field === field)?.operators.includes('prefix') ?? false
  const limitError = unique.some((v) => v.length > MAX_VALUE_LENGTH)
    ? `Each ${label} can be at most ${MAX_VALUE_LENGTH} characters.`
    : unique.length > maxValues
      ? `Too many values: ${unique.length}. Search up to ${maxValues} unique values at a time.`
      : null
  const error = formError ?? (multi ? limitError : null)

  // ── Grouped results → one flat option list (keyboard order = visual order) ──
  const view = useMemo(() => {
    const out: { cat: keyof CategoryData; items: GlobalSearchResult[]; more: Option | null }[] = []
    if (!single) return out
    const cats = tabCat ? [tabCat] : GROUP_ORDER
    for (const cat of cats) {
      // Filtered charts on All come from their own request; a tab's from its own.
      const src = cat === 'encounter' && filteredCharts && !tabCat
        ? (chartQOn ? chartQ.data : undefined)
        : tabCat ? tabData : allQ.data
      if (!src?.[cat]) continue
      const all = flattenResults(src, cat)
      const items = tabCat ? all : all.slice(0, ALL_SHOW)
      // A number only when exact (no "12+" next to a "20+" for the same rows).
      const total = src[cat].has_more ? null : src[cat].count
      const hasMore = !!src[cat].has_more || all.length > items.length
      if (items.length === 0) continue
      const id = `${listId}-more-${cat}`
      const more: Option | null = !hasMore || capped
        ? null
        : tab === 'all'
          ? { kind: 'more', id, tab: cat as SearchTab, action: 'tab', label: `See all${total === null ? '' : ` ${total}`} in ${TAB_LABEL[cat as SearchTab]}` }
          : { kind: 'more', id, tab: cat as SearchTab, action: 'expand', label: expanded && moreQ.isError && !moreQ.isFetching ? 'Couldn’t load more. Try again' : expanded ? 'Loading…' : 'Show more' }
      out.push({ cat, items, more })
    }
    return out
  }, [single, tabCat, tab, filteredCharts, chartQOn, chartQ.data, tabData, allQ.data, listId, capped, expanded, moreQ.isError, moreQ.isFetching])

  const flat: Option[] = useMemo(() => view.flatMap((g) => [
    ...g.items.map<Option>((r) => ({ kind: 'result', id: `${listId}-${r.id}`, result: r })),
    ...(g.more ? [g.more] : []),
  ]), [view, listId])
  const activeKey = `${q}|${tab}|${filteredCharts}`
  const active = activeFor.key === activeKey ? activeFor.idx : 0
  const setActive = (idx: number) => setActiveFor({ key: activeKey, idx })
  const activeIdx = flat.length ? Math.min(active, flat.length - 1) : -1
  const activeOpt = activeIdx >= 0 ? flat[activeIdx] : null

  useEffect(() => {
    if (activeOpt) document.getElementById(activeOpt.id)?.scrollIntoView?.({ block: 'nearest' })
  }, [activeOpt])

  const loading = single && (tabCat ? tabQ.isLoading : allQ.isLoading)
  const chartsBusy = filteredCharts && !tabCat && (chartQ.isLoading || chartQ.isPlaceholderData)
  // The rows on screen are another query's (debounce pending, or its request in flight): dimmed.
  const dim = (tabCat ? tabQOn && tabQ.isPlaceholderData : allQ.isPlaceholderData)
    || (chartQOn && chartQ.isPlaceholderData)
    || q !== (multi ? '' : trimmed)
  // Enter may only open a row that belongs to the text as typed now.
  const settled = single && !dim && !loading && !chartsBusy
  const enterKey = `${trimmed}|${tab}`
  const quickError = (tabCat ? tabQ.error : allQ.error) ?? (chartQOn ? chartQ.error : null)

  // One count per category, from the request whose rows are on screen: the filtered
  // chart request, else the open tab's own request (20, or 50 after Show more), else the All request
  // (up to 12). Another query's placeholder data never counts.
  const catCount = (k: keyof CategoryData): { n: number; more: boolean } | null => {
    if (k === 'encounter' && chipFiltered) {
      if (!filteredCharts) return null // another tab: the filtered chart count is not fetched
      const c = chartSrc.isPlaceholderData ? undefined : chartSrcData?.encounter
      return c ? { n: c.count, more: !!c.has_more } : null
    }
    const own = k === tabCat && !tabQ.isPlaceholderData ? tabData?.[k] : undefined
    const c = own ?? (allQ.isPlaceholderData ? undefined : allQ.data?.[k])
    return c ? { n: c.count, more: !!c.has_more } : null
  }
  // A tab count only when it is exact (no "+"): lower bounds from different
  // requests ("12+" on All, "20+" on Charts) read as jitter. All = the sum, so it
  // is shown only when every category's count is known and exact.
  const tabCount = (t: SearchTab): string | null => {
    if (!single) return null
    if (t !== 'all') {
      const c = catCount(t)
      return c && !c.more ? String(c.n) : null
    }
    if (!allQ.data || allQ.isPlaceholderData) return null
    const cs = CATEGORY_KEYS.map(catCount)
    if (cs.some((c) => !c || c.more)) return null
    return String(cs.reduce((s, c) => s + c!.n, 0))
  }
  // Filters (not the text) emptied Charts: the unfiltered request had chart rows.
  const filtersExclude = filteredCharts
    && catCount('encounter')?.n === 0
    && !allQ.isPlaceholderData && (allQ.data?.encounter.count ?? 0) > 0

  /** Expanded to the max and more still match. Spoken once through the live region below, not its own. */
  const capText = capped
    ? `Showing first ${SEARCH_MAX_LIMIT} — ${tabCat === 'encounter' && canFilter ? 'add a Facility or Status filter, or a longer search, to narrow' : 'add a filter or a longer search to narrow'}`
    : null

  // ── Live region: counts, debounced so it never re-reads controls ──
  const announceNow = !open
    ? ''
    : multi
      ? showBulk && bulk.data
        ? `${plural(bulk.data.total, 'chart')} found${bulk.data.summary[0]?.unmatched.length ? `, ${plural(bulk.data.summary[0].unmatched.length, 'identifier')} unmatched` : ''}`
        : `${plural(unique.length, 'identifier')} detected`
      : single && !loading && !dim
        ? flat.length ? `${tabCount(tab) ?? flat.length} results${capText ? `. ${capText}` : ''}` : 'No results'
        : ''
  const announce = useDebouncedValue(announceNow, 500)

  const bulkShown = multi && showBulk && !!request
  // Reopened over a held list: statuses may have moved since (a coder submitted a
  // chart from it), so fetch the page again. Same key: rows stay, the bar says Refreshing….
  const wasOpen = useRef(open)
  const refetchBulk = bulk.refetch
  useEffect(() => {
    if (open && !wasOpen.current && bulkShown) void refetchBulk()
    wasOpen.current = open
  }, [open, bulkShown, refetchBulk])
  const sent = bulkShown ? request.conditions[0] : null
  // After a bulk search the field reads as the list it searched, one line, read-only.
  const summary = sent
    ? `${plural(sent.values.length, FIELD_LABELS[sent.field])}: ${sent.values.slice(0, 2).join(', ')}${sent.values.length > 2 ? ', …' : ''}`
    : null

  // ── Auto-grow the field in list mode (JS, so it works where CSS field-sizing does not) ──
  useLayoutEffect(() => {
    const el = fieldRef.current
    if (!el) return
    el.style.height = ''
    if (!multi || summary) return // one line
    if (el.scrollHeight <= el.clientHeight + 1) return
    // scrollHeight can be in zoomed px under the app's content zoom; scale by
    // computed (CSS px) ÷ offset so the height is exact anyway.
    const k = el.offsetHeight ? parseFloat(getComputedStyle(el).height) / el.offsetHeight : 1
    const borders = el.offsetHeight - el.clientHeight
    el.style.height = `${Math.min(Math.ceil((el.scrollHeight + borders) * k) + 1, FIELD_MAX)}px`
  }, [text, multi, summary, open])

  // ── Place the dropdown: right edge on the field's right edge, clamped inside the viewport ──
  useLayoutEffect(() => {
    if (!open) return
    function place() {
      const a = anchorRef.current
      const p = panelRef.current
      if (!a || !p) return
      // Phones: CSS spans the dropdown across the header.
      if (window.matchMedia?.('(max-width: 639px)').matches) return setPanelLeft(null)
      const r = a.getBoundingClientRect()
      // Rects are in screen px; offsets are CSS px under the app's content zoom.
      const k = a.offsetWidth ? r.width / a.offsetWidth : 1
      const w = p.offsetWidth * k
      const gutter = PANEL_GUTTER * k
      const x = Math.max(gutter, Math.min(r.right - w, window.innerWidth - gutter - w))
      setPanelLeft((x - r.left) / k)
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open])

  // Warm the routes a result opens, so the first open after a search is instant.
  useEffect(() => {
    if (!open) return
    void import('@/features/workspace/routes')
  }, [open])

  // Cmd/Ctrl+K focuses the header field (chart review keeps it as "next chart").
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'k' || e.altKey || e.shiftKey) return
      if (onChartReview) return
      const el = fieldRef.current
      if (!el) return
      e.preventDefault()
      const from = document.activeElement as HTMLElement | null
      if (from && !rootRef.current?.contains(from)) returnTo.current = from
      if (document.activeElement === el) useSearchStore.getState().patch({ open: true })
      else el.focus() // opens the dropdown (onFocus)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onChartReview])

  // Opened from elsewhere (an old /search?q= bookmark): typing goes to the field.
  useEffect(() => {
    const el = fieldRef.current
    if (!open || !el || rootRef.current?.contains(document.activeElement)) return
    quiet.current = true
    el.focus()
    quiet.current = false
    el.setSelectionRange(el.value.length, el.value.length)
  }, [open])

  // Outside click closes the dropdown (the field keeps its text).
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (rootRef.current?.contains(e.target as Node)) return
      pendingEnter.current = null
      returnTo.current = null // the user went somewhere else on purpose
      setSection(null)
      useSearchStore.getState().patch({ open: false })
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Programmatic focus after a render: after a bulk search, and after Edit.
  useLayoutEffect(() => {
    const el = fieldRef.current
    const how = focusNext.current
    if (!how || !el) return
    focusNext.current = null
    quietFocus()
    if (how === 'end') el.setSelectionRange(el.value.length, el.value.length)
  })

  function quietFocus() {
    quiet.current = true
    fieldRef.current?.focus()
    quiet.current = false
  }

  function close() {
    // A held Enter never outlives the dropdown: dismissing it cancels the deferred open.
    pendingEnter.current = null
    setSection(null)
    patch({ open: false })
  }

  /** Leave the field: back to where Cmd/Ctrl+K was pressed, else just blur. */
  function leave() {
    const back = returnTo.current
    returnTo.current = null
    if (back?.isConnected) back.focus()
    else fieldRef.current?.blur()
  }

  function closeAndLeave() {
    close()
    leave()
  }

  /** A quick-search result was opened in this tab: the field empties, like any app search. */
  function opened() {
    patch({ text: '', commaAs: null, showBulk: false, request: null })
    closeAndLeave()
  }

  function openResult(r: GlobalSearchResult, newTab = false) {
    const route = r.category === 'encounter' ? chartRoute(r) : getResultRoute(r)
    if (!route) return
    if (newTab) {
      window.open(route, '_blank', 'noopener')
      return
    }
    void navigate(route)
    opened()
  }

  /** Every "more" row acts inside the panel: never a navigation. */
  function activate(o: Option, newTab = false) {
    if (o.kind === 'result') return openResult(o.result, newTab)
    focusNext.current = 'keep' // a clicked row may vanish: keyboard stays in the field
    if (o.action === 'tab') patch({ tab: o.tab })
    else if (expanded && moreQ.isError) { if (!moreQ.isFetching) void moreQ.refetch() }
    else setExpandedKey(moreKey) // the highlight stays put: on the first new row once they load
  }

  function enter(newTab: boolean) {
    if (activeOpt) activate(activeOpt, newTab)
  }

  // A pending Enter runs once the results are this text's; typing on cancels it.
  useEffect(() => {
    const p = pendingEnter.current
    if (!p) return
    // Closed by any path (Escape, outside click, Tab away): the queries keep resolving, the Enter must not.
    if (!open) {
      pendingEnter.current = null
      return
    }
    if (p.key !== enterKey) pendingEnter.current = null
    else if (settled) {
      pendingEnter.current = null
      enter(p.newTab)
    }
  })

  /** Every text edit goes through here: a text that is no longer a list drops the old bulk request. */
  function setText(value: string, nextCommaAs: CommaAs = listKind(value) === 'commas' ? commaAs : null) {
    setFormError(null)
    setSection(null) // a new query: an open checklist would push its results down
    const list = canFilter && isIdentifierList(value, nextCommaAs)
    patch({ open: true, text: value, commaAs: nextCommaAs, showBulk: false, ...(list ? {} : { request: null }) })
  }

  function runBulk() {
    if (!field) return
    if (unique.length === 0) return setFormError(`Add at least one ${label}.`)
    if (bulkOp === 'prefix') {
      if (!prefixAllowed) return setFormError(`“Starts with” is not available for ${label}.`)
      if (unique.some((v) => v.length < PREFIX_MIN_LEN)) {
        return setFormError(`“Starts with” needs at least ${PREFIX_MIN_LEN} characters per ${label}.`)
      }
      if (field === 'med_id' && unique.some((v) => 'med_'.startsWith(v.toLowerCase()))) {
        return setFormError("A Med ID prefix must go past 'med_'.")
      }
    }
    if (limitError) return setFormError(limitError)
    const next: ChartIdSearchRequest = {
      // The de-duplicated list, so the backend's raw-count cap never trips on a
      // pasted column that repeats ids.
      conditions: [{ field, operator: bulkOp, values: unique }],
      filters: buildFilters(),
      page: 1,
      page_size: CHART_ID_PAGE_SIZE,
    }
    // Same request again: the query key is unchanged, so run it again explicitly.
    if (JSON.stringify(next) === JSON.stringify(request)) {
      void queryClient.refetchQueries({ queryKey: ['chart-id-search', request] })
    }
    focusNext.current = 'keep' // the Search button goes away: focus stays in the header field
    setSection(null) // the results take the space an open checklist had
    patch({ request: next, showBulk: true })
  }

  /** Back from the searched list to editing it, in the same field. */
  function editIds() {
    focusNext.current = 'end'
    patch({ open: true, showBulk: false })
  }

  // Filter changes apply at once to results already on screen (bulk), never to an unsent list.
  function setFilters(p: { facilityIds?: string[]; buckets?: ChartBucketKey[]; match?: ChartIdOperator | null }) {
    setFormError(null)
    const next = { ...p } as Partial<typeof store>
    // Single mode: the filtered quick search re-runs from the chips (query keys).
    if (multi && showBulk && request && p.match === undefined) {
      next.request = {
        ...request,
        filters: buildFilters(p.facilityIds ?? facilityIds, p.buckets ?? buckets),
        page: 1,
      }
    } else if (multi && showBulk && p.match !== undefined) {
      next.showBulk = false // a new operator needs a new Search
    }
    patch(next)
  }

  function onFieldKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.nativeEvent.isComposing) return
    if (summary) {
      // The searched list, read-only: Enter (or F2) edits it; Down walks the result rows.
      if ((e.key === 'Enter' && !e.metaKey && !e.ctrlKey) || e.key === 'F2') {
        e.preventDefault()
        editIds()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (!open) patch({ open: true })
        else panelRef.current?.querySelector<HTMLElement>('.cid-rows a')?.focus()
      }
      return
    }
    if (multi) {
      // Enter searches the list; Shift+Enter is a new line (Cmd/Ctrl+Enter: handled on the root).
      if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        runBulk()
      }
      return // arrows move the caret
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault()
      patch({ open: true })
    } else if (e.key === 'Enter' && e.shiftKey && canFilter) {
      // Shift+Enter starts a second line: typing a list by hand.
    } else if (e.key === 'Enter') {
      e.preventDefault() // one line: no newlines in single mode
      if (!open) patch({ open: true })
      const newTab = e.metaKey || e.ctrlKey
      // Never open a row from the previous query: wait for this text's results.
      if (settled || trimmed.length < 2) enter(newTab)
      else pendingEnter.current = { key: enterKey, newTab }
    } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && flat.length) {
      e.preventDefault()
      const step = e.key === 'ArrowDown' ? 1 : -1
      setActive((activeIdx + step + flat.length) % flat.length)
    }
  }

  /** Escape peels one layer at a time: checklist → dropdown → the field itself. */
  function onRootKey(e: React.KeyboardEvent) {
    if (e.nativeEvent.isComposing) return
    if (e.key === 'Escape') {
      e.preventDefault()
      if (section) {
        const s = section
        setSection(null)
        chipRefs.current[s]?.focus()
      } else if (open) {
        const inPanel = panelRef.current?.contains(document.activeElement) ?? false
        close()
        if (inPanel) quietFocus()
      } else {
        leave()
      }
      return
    }
    // ID editor: Cmd/Ctrl+Enter searches from anywhere in the search (field, chips,
    // checklists). On the results it is left to the rows (open in a new tab).
    if (multi && !bulkShown && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      runBulk()
    }
  }

  // Tablist: roving tabindex, arrows move and select.
  function onTabKey(e: React.KeyboardEvent, t: SearchTab) {
    const i = TABS.indexOf(t)
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const n = TABS[(i + step + TABS.length) % TABS.length]
    patch({ tab: n })
    tabRefs.current[n]?.focus()
  }

  // Chart filters only where charts are: the Charts tab, All with chart rows (or a
  // filter already applied, so it can be cleared), and bulk. Never beside codes only.
  // Single mode needs no ID field: the filters narrow the quick search.
  const chartRows = view.some((g) => g.cat === 'encounter')
  const showFilters = (multi && chartIds)
    || (canFilter && single && (tab === 'encounter' || (tab === 'all' && (chartRows || chipFiltered))))
  const commaCount = commaText && !multi ? uniqueIdentifiers(parseIdentifiers(text)).length : 0
  // Name only the ID fields this user can search (an engineer without a grant: Med ID only).
  const idWords = !canFilter
    ? null
    : !options.data
      ? 'IDs'
      : fields.map((f) => FIELD_LABELS[f]).join(', ') || null

  return (
    <div
      ref={rootRef}
      className={`hs${open ? ' hs--open' : ''}${multi && !summary ? ' hs--multi' : ''}`}
      data-tour="header.search"
      onKeyDown={onRootKey}
      onBlur={(e) => {
        // Tabbing out of the search closes it (a click outside is handled on mousedown).
        const to = e.relatedTarget as Node | null
        if (!to || rootRef.current?.contains(to)) return
        returnTo.current = null
        if (open) close()
      }}
    >
      <div ref={anchorRef} className="hs-anchor">
        <div className="hs-box" onPointerEnter={prefetch}>
          <Search size={14} className="hs-icon" aria-hidden="true" />
          <textarea
            ref={fieldRef}
            className={`hs-input${summary ? ' hs-input--summary' : ''}`}
            rows={1}
            value={summary ?? text}
            readOnly={!!summary}
            spellCheck={false}
            autoComplete="off"
            role="combobox"
            aria-label="Search codes, charts and IDs"
            aria-autocomplete="list"
            aria-expanded={open}
            // Only while the listbox is rendered (single mode): never a dangling IDREF.
            aria-controls={open && single ? listId : undefined}
            aria-activedescendant={open && single && activeOpt && !dim ? activeOpt.id : undefined}
            aria-keyshortcuts={onChartReview ? undefined : 'Control+K Meta+K'}
            placeholder={canFilter ? 'Search codes, charts, IDs…' : 'Search codes, providers…'}
            onFocus={() => {
              prefetch()
              if (!quiet.current) patch({ open: true })
            }}
            onMouseDown={() => {
              // Closed over a searched list: reopen its results; open: edit the list.
              if (!open) patch({ open: true })
              else if (summary) editIds()
            }}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onFieldKey}
          />
          {/* The summary is drawn here, not in the textarea: a textarea scrolls to its
              caret and can't ellipsize, which clipped the START of long values. */}
          {summary && <span className="hs-summary" aria-hidden="true">{summary}</span>}
          {!text && !onChartReview && <kbd className="hs-kbd" aria-hidden="true">{MOD_KEY} K</kbd>}
          {/* Not over the searched list: discarding it goes through Edit, never a stray tap. */}
          {text && !summary && (
            <button
              type="button"
              className="hs-btn hs-clear"
              aria-label="Clear search text"
              onClick={() => { focusNext.current = 'end'; setText('') }}
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
          {summary && sent && (
            <button
              type="button"
              className="cid-link hs-edit"
              aria-label={`Edit the ${plural(sent.values.length, FIELD_LABELS[sent.field])}`}
              onClick={editIds}
            >
              Edit
            </button>
          )}
          {/* Phones: the expanded field's way out without a keyboard. */}
          {open && (
            <button type="button" className="cid-link hs-cancel" onClick={closeAndLeave}>
              Cancel
            </button>
          )}
        </div>

        {open && (
          <div
            ref={panelRef}
            className="sp"
            style={panelLeft === null ? undefined : { left: panelLeft, right: 'auto' }}
            onMouseDown={(e) => {
              // A click on the dropdown's background keeps focus (and the caret) in the field.
              const t = e.target as HTMLElement
              if (!t.closest('button, a, input, label, textarea, [role="option"]')) e.preventDefault()
            }}
          >
            <div className="sp-body">
              {!multi && trimmed.length < 2 && (
                <p className="sp-help">
                  Type a code, {idWords && idWords !== 'IDs' ? `${idWords}, ` : ''}provider or facility.
                  {idWords && ' Paste a list of IDs — one per line or comma-separated — to search them together.'}
                </p>
              )}

              {(single || (!multi && trimmed.length >= 2)) && (
                <div className="sp-tabs" role="tablist" aria-label="Result categories">
                  {TABS.map((t) => {
                    const n = tabCount(t)
                    return (
                      <button
                        key={t}
                        ref={(el) => { tabRefs.current[t] = el }}
                        type="button"
                        role="tab"
                        aria-selected={tab === t}
                        aria-controls={listId}
                        tabIndex={tab === t ? 0 : -1}
                        className={`sp-tab${tab === t ? ' active' : ''}`}
                        onClick={() => patch({ tab: t })}
                        onKeyDown={(e) => onTabKey(e, t)}
                      >
                        {TAB_LABEL[t]}
                        {n && n !== '0' && <span className="sp-tab-count">{n}</span>}
                      </button>
                    )
                  })}
                </div>
              )}

              {multi && (!bulkShown || !field) && (
                <div className="sp-multi">
                  {!bulkShown && (
                    <p className="sp-count">
                      <strong>
                        {unique.length === maxValues
                          ? `${maxValues} identifiers ready to search`
                          : `${plural(unique.length, 'identifier')} detected`}
                      </strong>
                      {duplicates > 0 && ` · ${plural(duplicates, 'duplicate')} removed`}
                    </p>
                  )}
                  {options.isLoading ? (
                    <p className="sp-muted">Loading…</p>
                  ) : options.isError ? (
                    <p className="sp-error" role="alert">{apiErrorMessage(options.error, 'Couldn’t load search options.')}</p>
                  ) : !field ? (
                    <p className="sp-muted">There are no chart identifiers you can search with your access.</p>
                  ) : null}
                </div>
              )}

              {showFilters && (
                <FilterRow
                  uid={uid}
                  section={section}
                  setSection={setSection}
                  chipRefs={chipRefs}
                  facilities={options.data?.facilities ?? []}
                  bucketOptions={options.data?.buckets ?? []}
                  facilityIds={facilityIds}
                  buckets={buckets}
                  // Match (Exact / Starts with) is a bulk-list option; the quick search has one matcher.
                  match={multi ? match : undefined}
                  effectiveMatch={bulkOp}
                  prefixAllowed={prefixAllowed}
                  onChange={setFilters}
                  lead={multi && field ? (
                    // Search by + filters share one line where the width allows.
                    <div className="sp-field-row">
                      <span className="sp-label" id={`${uid}-by`}>Search by</span>
                      {fields.length > 1 ? (
                        <FieldChips fields={fields} value={field} labelledBy={`${uid}-by`} onChange={(f) => { setFormError(null); patch({ field: f, showBulk: false }) }} />
                      ) : (
                        <span className="sp-static" aria-labelledby={`${uid}-by`}>{label}</span>
                      )}
                    </div>
                  ) : null}
                />
              )}

              {commaCount >= 2 && (
                <p className="sp-note">
                  Commas in your text.{' '}
                  <button type="button" className="cid-link" onClick={() => setText(text, 'ids')}>
                    Search as {commaCount} IDs
                  </button>
                </p>
              )}

              {multi && field && !bulkShown && (
                <div className="sp-submit-row">
                  {error && <p className="sp-error" role="alert">{error}</p>}
                  <button
                    type="button"
                    className="btn btn-primary btn-sm sp-submit"
                    disabled={!!limitError || unique.length === 0}
                    onClick={runBulk}
                  >
                    Search {plural(unique.length, 'ID')}
                  </button>
                </div>
              )}

              {bulkShown && (
                <ChartIdResults
                  request={request}
                  search={bulk}
                  // A coder works through a pasted list: the list, its results and page stay
                  // (memory only) for the next open, until edited, cleared or the identity changes.
                  onOpen={closeAndLeave}
                  onPage={(page) => patch({ request: { ...request, page } })}
                />
              )}

              {single && (
                <>
                  {quickError && !dim && (
                    <p className="sp-error sp-pad" role="alert">{apiErrorMessage(quickError, 'Search failed.')}</p>
                  )}
                  {loading && !flat.length && !dim && <div className="sp-skeleton" aria-hidden="true"><span /><span /><span /></div>}
                  {!loading && !dim && !chartsBusy && !quickError && (filtersExclude || !flat.length) && (
                    filtersExclude ? (
                      <p className={flat.length ? 'sp-note' : 'sp-empty'}>
                        {/* One way out: the chip bar's Clear filters, right above (L-11). */}
                        No charts match these filters.
                      </p>
                    ) : (
                      <p className="sp-empty">No results for &ldquo;{q}&rdquo;</p>
                    )
                  )}
                  <ResultList
                    listId={listId}
                    view={view}
                    query={q}
                    activeId={activeOpt?.id}
                    busy={dim}
                    onHover={(id) => setActive(flat.findIndex((o) => o.id === id))}
                    onActivate={(o) => activate(o)}
                    onLinkOpen={opened}
                  />
                  {capText && !dim && <p className="sp-help">{capText}</p>}
                </>
              )}
            </div>

            {/* Only the keys that do something right now. */}
            <div className="sp-foot" aria-hidden="true">
              {bulkShown ? (
                bulk.data?.results.length ? (
                  <>
                    <span><kbd>Up/Down</kbd> Move</span>
                    <span><kbd>Enter</kbd> Open</span>
                    <span><kbd>{MOD_KEY}+Enter</kbd> New tab</span>
                  </>
                ) : null
              ) : multi ? (
                <>
                  {field && unique.length > 0 && !limitError && <span><kbd>Enter</kbd> Search</span>}
                  <span><kbd>Shift+Enter</kbd> New line</span>
                </>
              ) : activeOpt ? (
                <>
                  <span><kbd>Enter</kbd> Open</span>
                  <span><kbd>Up/Down</kbd> Move</span>
                  {activeOpt.kind === 'result' && <span><kbd>{MOD_KEY}+Enter</kbd> New tab</span>}
                </>
              ) : null}
              <span><kbd>Esc</kbd> Close</span>
            </div>
          </div>
        )}
      </div>
      <div role="status" aria-live="polite" className="sr-only">{announce}</div>
    </div>
  )
}

/** Field chips (radiogroup): arrows move and select, one Tab stop. */
function FieldChips({ fields, value, labelledBy, onChange }: {
  fields: IdField[]; value: IdField; labelledBy: string; onChange: (f: IdField) => void
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  return (
    <div className="sp-chips" role="radiogroup" aria-labelledby={labelledBy}>
      {fields.map((f, i) => (
        <button
          key={f}
          ref={(el) => { refs.current[i] = el }}
          type="button"
          role="radio"
          aria-checked={f === value}
          tabIndex={f === value ? 0 : -1}
          className={`sp-chip${f === value ? ' applied' : ''}`}
          onClick={() => onChange(f)}
          onKeyDown={(e) => {
            const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
            if (!step) return
            e.preventDefault()
            const n = (i + step + fields.length) % fields.length
            onChange(fields[n])
            refs.current[n]?.focus()
          }}
        >
          {FIELD_LABELS[f]}
        </button>
      ))}
    </div>
  )
}

function ResultList({ listId, view, query, activeId, busy, onHover, onActivate, onLinkOpen }: {
  listId: string
  view: { cat: keyof CategoryData; items: GlobalSearchResult[]; more: Option | null }[]
  query: string
  activeId: string | undefined
  busy: boolean
  onHover: (id: string) => void
  onActivate: (o: Option) => void
  onLinkOpen: () => void
}) {
  const chartRow = useChartRow()
  // Plain click opens here (and closes the panel); modified clicks keep the
  // browser's own new-tab behavior.
  const onLinkClick = (e: React.MouseEvent) => {
    if (!e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) onLinkOpen()
  }
  return (
    <div id={listId} role="listbox" aria-label="Search results" className="sp-results" aria-busy={busy}>
      {/* Rows from the previous query would read as results for this one: skeleton instead. */}
      {busy && <div className="sp-skeleton" aria-hidden="true"><span /><span /><span /></div>}
      {!busy && view.map((g) => {
        const headId = `${listId}-h-${g.cat}`
        return (
          <div key={g.cat} role="group" aria-labelledby={headId} className="sp-group">
            <div id={headId} className="sp-group-head">{TAB_LABEL[g.cat as SearchTab]}</div>
            {g.items.map((r) => {
              const id = `${listId}-${r.id}`
              const common = {
                id,
                role: 'option' as const,
                'aria-selected': id === activeId,
                tabIndex: -1,
                onMouseMove: () => { if (id !== activeId) onHover(id) },
                onClick: onLinkClick,
              }
              if (r.category === 'encounter') {
                const { content, label } = chartRow(r, query)
                return (
                  <Link key={r.id} to={chartRoute(r)} className="sp-row sp-row--chart" aria-label={label} {...common}>
                    {content}
                  </Link>
                )
              }
              return (
                <Link key={r.id} to={getResultRoute(r) ?? '#'} className="sp-row" {...common}>
                  <span className="sp-row-primary"><HighlightMatch text={r.title} query={query} /></span>
                  <span className="sp-row-meta">
                    <HighlightMatch text={r.subtitle} query={query} />
                    {r.category === 'provider' && r.extra ? ` · NPI ${r.extra}` : ''}
                  </span>
                  {r.status === 'inactive' && <span className="sp-row-status status-badge muted">Inactive</span>}
                </Link>
              )
            })}
            {g.more && (
              <div
                id={g.more.id}
                role="option"
                aria-selected={g.more.id === activeId}
                tabIndex={-1}
                className="sp-row sp-row--more"
                onMouseMove={() => { if (g.more!.id !== activeId) onHover(g.more!.id) }}
                onClick={() => onActivate(g.more!)}
              >
                {g.more.kind === 'more' && g.more.label}
                {g.more.kind === 'more' && g.more.action === 'tab' && <> <span aria-hidden="true">→</span></>}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}


/**
 * Facility / Status / Match chips. A chip toggles its checklist inside the
 * dropdown (no portal, so nothing clips). Selections apply at once; Escape
 * (handled on the search root) closes the checklist and returns to its chip.
 */
function FilterRow({
  uid, section, setSection, chipRefs, facilities, bucketOptions, facilityIds, buckets,
  match, effectiveMatch, prefixAllowed, onChange, lead,
}: {
  uid: string
  section: Section | null
  setSection: (s: Section | null) => void
  chipRefs: React.MutableRefObject<Partial<Record<Section, HTMLButtonElement | null>>>
  facilities: { id: string; name: string; is_active: boolean }[]
  /** Chart Queue / History tabs this user may filter on, in tab order. */
  bucketOptions: ChartBucketKey[]
  facilityIds: string[]
  buckets: ChartBucketKey[]
  /** undefined = no Match chip (single mode: the quick search has one matcher). */
  match: ChartIdOperator | null | undefined
  effectiveMatch: ChartIdOperator
  prefixAllowed: boolean
  onChange: (p: { facilityIds?: string[]; buckets?: ChartBucketKey[]; match?: ChartIdOperator | null }) => void
  /** Shown before the chips on the same line (bulk: Search by). */
  lead?: ReactNode
}) {
  const facilityName = new Map(facilities.map((f) => [f.id, f.name]))
  const summary = (ids: string[], name: (v: string) => string) =>
    ids.length === 0 ? 'All' : ids.length === 1 ? name(ids[0]) : `${ids.length} selected`
  const chips: { key: Section; label: string; value: string; applied: boolean; clear: () => void }[] = [
    {
      key: 'facility', label: 'Facility',
      value: summary(facilityIds, (v) => facilityName.get(v) ?? '1 facility'),
      applied: facilityIds.length > 0, clear: () => onChange({ facilityIds: [] }),
    },
    {
      key: 'status', label: 'Status',
      value: summary(buckets, (v) => CHART_BUCKET_LABELS[v as ChartBucketKey] ?? v),
      applied: buckets.length > 0, clear: () => onChange({ buckets: [] }),
    },
    ...(match === undefined ? [] : [
      { key: 'match' as const, label: 'Match', value: effectiveMatch === 'prefix' ? 'Starts with' : 'Exact', applied: match !== null, clear: () => onChange({ match: null }) },
    ]),
  ]
  const anyApplied = chips.some((c) => c.applied)

  return (
    <div className="sp-filter-wrap">
      <div className="sp-filter-line">
        {lead}
        <div className="sp-filters" role="group" aria-label="Chart filters">
          {chips.map((c) => (
            <span key={c.key} className={`sp-chip-wrap${c.applied ? ' applied' : ''}`}>
              <button
                ref={(el) => { chipRefs.current[c.key] = el }}
                type="button"
                className={`sp-chip${c.applied ? ' applied' : ''}`}
                aria-expanded={section === c.key}
                aria-controls={`${uid}-sec-${c.key}`}
                // Long values ellipsize (max-width) so the row stays one line; full text on hover.
                title={c.value.length > 16 ? `${c.label}: ${c.value}` : undefined}
                aria-label={`${c.label}: ${c.value}`} // the split value must read as one name
                onClick={() => setSection(section === c.key ? null : c.key)}
              >
                {/* Facility names share long prefixes ("CC005_OSFW_…"): the start ellipsizes by
                    measured width (CSS), the end that tells them apart never does. */}
                {c.label}:{' '}
                <span className="sp-chip-value">
                  {c.value.length > CHIP_TAIL + 4 ? (
                    <>
                      <span className="sp-chip-start">{c.value.slice(0, -CHIP_TAIL)}</span>
                      <span className="sp-chip-end">{c.value.slice(-CHIP_TAIL)}</span>
                    </>
                  ) : <span className="sp-chip-start">{c.value}</span>}
                </span>
                <ChevronDown size={12} aria-hidden="true" className={section === c.key ? 'sp-flip' : undefined} />
              </button>
              {c.applied && (
                <button type="button" className="sp-chip-x" aria-label={`Remove ${c.label.toLowerCase()} filter`} onClick={c.clear}>
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
          {anyApplied && (
            <button type="button" className="cid-link sp-clear-filters" onClick={() => onChange({ facilityIds: [], buckets: [], match: null })}>
              Clear filters
            </button>
          )}
        </div>
      </div>
      {section === 'facility' && (
        <Checklist
          id={`${uid}-sec-facility`}
          legend="Facilities"
          options={facilities.map((f) => ({ value: f.id, label: f.name, badge: f.is_active ? undefined : 'inactive' }))}
          value={facilityIds}
          onChange={(v) => onChange({ facilityIds: v })}
        />
      )}
      {section === 'status' && (
        <Checklist
          id={`${uid}-sec-status`}
          legend="Statuses"
          options={bucketOptions.map((b) => ({ value: b, label: CHART_BUCKET_LABELS[b] }))}
          value={buckets}
          onChange={(v) => onChange({ buckets: v as ChartBucketKey[] })}
        />
      )}
      {section === 'match' && (
        <div id={`${uid}-sec-match`} className="sp-section">
          <fieldset className="sp-checks sp-checks--inline">
            <legend className="sr-only">Match</legend>
            <label className="sp-check">
              <input
                type="radio"
                name={`${uid}-match`}
                autoFocus={effectiveMatch === 'exact'}
                checked={effectiveMatch === 'exact'}
                onChange={() => onChange({ match: 'exact' })}
              />
              Exact
            </label>
            <label className={`sp-check${prefixAllowed ? '' : ' disabled'}`}>
              <input
                type="radio"
                name={`${uid}-match`}
                autoFocus={effectiveMatch === 'prefix'}
                disabled={!prefixAllowed}
                checked={effectiveMatch === 'prefix'}
                onChange={() => onChange({ match: 'prefix' })}
              />
              Starts with
            </label>
          </fieldset>
        </div>
      )}
    </div>
  )
}

/**
 * One column, one row per option: a long name wraps to two lines (then
 * ellipsizes, full name on hover); a badge (inactive) sits outside the label so
 * it is never cut. What was selected when it opened is pinned to the top — the order is
 * fixed while it is open, so a row never jumps under the pointer. A filter box
 * appears above 8 options. One Tab stop: arrows move, Space toggles.
 */
function Checklist({ id, legend, options, value, onChange }: {
  id: string
  legend: string
  options: { value: string; label: string; badge?: string }[]
  value: string[]
  onChange: (v: string[]) => void
}) {
  const [filter, setFilter] = useState('')
  const [pinned] = useState(() => new Set(value))
  const [cursor, setCursor] = useState(0)
  const boxes = useRef<(HTMLInputElement | null)[]>([])
  const filterRef = useRef<HTMLInputElement>(null)
  const filterable = options.length > 8
  const ordered = useMemo(
    () => [...options.filter((o) => pinned.has(o.value)), ...options.filter((o) => !pinned.has(o.value))],
    [options, pinned],
  )
  const needle = filter.trim().toLowerCase()
  const shown = needle ? ordered.filter((o) => o.label.toLowerCase().includes(needle)) : ordered
  const active = Math.max(0, Math.min(cursor, shown.length - 1))
  const move = (i: number) => {
    const n = Math.max(0, Math.min(i, shown.length - 1))
    setCursor(n)
    boxes.current[n]?.focus()
  }
  function onListKey(e: React.KeyboardEvent) {
    const keys: Record<string, number> = { ArrowDown: active + 1, ArrowUp: active - 1, Home: 0, End: shown.length - 1 }
    if (!(e.key in keys)) return
    e.preventDefault()
    if (e.key === 'ArrowUp' && active === 0 && filterable) filterRef.current?.focus()
    else move(keys[e.key])
  }
  return (
    <div id={id} className="sp-section">
      {filterable && (
        <input
          ref={filterRef}
          className="form-input sp-section-filter"
          placeholder={`Filter ${legend.toLowerCase()}`}
          aria-label={`Filter ${legend.toLowerCase()}`}
          value={filter}
          autoFocus
          onChange={(e) => { setFilter(e.target.value); setCursor(0) }}
          onKeyDown={(e) => {
            if (e.key !== 'ArrowDown') return
            e.preventDefault()
            move(0)
          }}
        />
      )}
      <fieldset className="sp-checks" onKeyDown={onListKey}>
        <legend className="sr-only">{legend}</legend>
        {shown.map((o, i) => (
          <label key={o.value} className="sp-check" title={o.badge ? `${o.label} (${o.badge})` : o.label}>
            <input
              ref={(el) => { boxes.current[i] = el }}
              type="checkbox"
              tabIndex={i === active ? 0 : -1}
              autoFocus={!filterable && i === 0}
              checked={value.includes(o.value)}
              onFocus={() => setCursor(i)}
              onChange={(e) => onChange(e.target.checked ? [...value, o.value] : value.filter((v) => v !== o.value))}
            />
            <span className="sp-check-label">{o.label}</span>
            {/* The space keeps the accessible name "Name inactive" (not "Nameinactive"). */}
            {o.badge && <>{' '}<span className="sp-check-badge">{o.badge}</span></>}
          </label>
        ))}
        {shown.length === 0 && <span className="sp-muted sp-check-empty">No matches</span>}
      </fieldset>
    </div>
  )
}
